import { WEDDING_EVENT } from '../../utils/calendar.jsx';
import { dayKey } from '../timeline/timelineData.js';

// The site is one page in five states. Which one it's in is derived from the
// synced calendar rather than a hardcoded date, so adding a Friday melse moves
// the switch to Friday without a code change. WEDDING_EVENT is the fallback
// for before the schedule has loaded, or if the feed is unreachable.
//
//   invitation  → months out: the game and the RSVP
//   approaching → the final fortnight: the schedule moves to the front
//   eventday    → a day with events on it: the day-of hub
//   live        → an event is actually running: the livestream leads
//   after       → everything has finished: the gallery leads
export const PHASES = ['invitation', 'approaching', 'eventday', 'live', 'after'];

// How early the schedule takes over the home page. Two weeks out the RSVP
// has done its job and what guests come back for is logistics — when things
// start, where they are — so the hub leads from there on.
const APPROACH_MS = 14 * 24 * 60 * 60 * 1000;

export function icsUtcToMs(value) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value || '');
  if (!m) return NaN;
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
}

export function derivePhase(now, events) {
  const source = events && events.length ? events : [WEDDING_EVENT];

  const spans = source
    .map((e) => {
      const start = icsUtcToMs(e.startUtc);
      const end = icsUtcToMs(e.endUtc);
      return {
        start,
        end: Number.isFinite(end) ? end : start,
        allDay: !!e.allDay,
      };
    })
    .filter((s) => Number.isFinite(s.start));

  if (!spans.length) return 'invitation';

  const first = Math.min(...spans.map((s) => s.start));
  const last = Math.max(...spans.map((s) => s.end));

  if (now >= last) return 'after';

  // "Happening now" means an actual timed event. An all-day entry is the
  // day's chapter heading, not something in progress — treating it as live
  // would put the livestream front and centre from midnight onwards.
  const running = spans.some((s) => !s.allDay && now >= s.start && now < s.end);
  if (running) return 'live';

  // Any day carrying an event — including an all-day chapter — is a day-of
  // day, so the hub is up from the morning rather than only once something
  // has started. All-day entries span [start, end) so each covered day counts.
  const today = dayKey(new Date(now));
  const isEventDay = spans.some((s) => {
    if (!s.allDay) return dayKey(new Date(s.start)) === today;
    for (let at = s.start; at < Math.max(s.end, s.start + 1); at += 86400000) {
      if (dayKey(new Date(at)) === today) return true;
    }
    return false;
  });
  if (isEventDay) return 'eventday';

  if (first - now <= APPROACH_MS) return 'approaching';
  return 'invitation';
}

// The wedding is months out, so the day-of states can't be checked by waiting
// for them. `?phase=live` (or eventday / approaching / after / invitation) on
// any URL forces one for previewing. A query param rather than anything
// visible: guests never see it, and there's no scaffolding left in the page.
function previewOverride() {
  try {
    const value = new URLSearchParams(window.location.search).get('phase');
    return value && PHASES.includes(value) ? value : null;
  } catch {
    return null;
  }
}

// The wedding is over, so the site stays in `after` for good rather than
// trusting the calendar to agree — anything left on it (a later entry, a
// stray end time) would otherwise hold the home page in a day-of state.
// derivePhase is kept in case the calendar-driven switch is ever wanted again.
const PINNED_PHASE = 'after';

// The phase used for rendering. `?phase=` still wins, so the other states
// can be previewed.
export function resolvePhase() {
  return previewOverride() || PINNED_PHASE;
}

// The couple's own name for what today is, taken from the all-day entry
// covering it — "Melse", "The Wedding Day". They title those entries to suit
// the day, so this is better than anything the site could word itself.
// Returns null on a day with no all-day entry, and the caller falls back.
export function allDayTitleFor(now, events) {
  if (!events || !events.length) return null;
  const today = dayKey(new Date(now));
  for (const event of events) {
    if (!event.allDay || !event.title) continue;
    const start = icsUtcToMs(event.startUtc);
    if (!Number.isFinite(start)) continue;
    const end = icsUtcToMs(event.endUtc);
    // All-day DTEND is exclusive, so walk [start, end) a day at a time and
    // always cover the start day when DTEND is missing or equal.
    const stop = Number.isFinite(end) ? Math.max(end, start + 1) : start + 1;
    let guard = 0;
    for (let at = start; at < stop && guard < 40; at += 86400000, guard += 1) {
      if (dayKey(new Date(at)) === today) return event.title;
    }
  }
  return null;
}

// Where the site is pointing right now. The hub leads with this, and it's
// also where the game hands you off when you finish. Only the invitation
// state points at the RSVP: from the final fortnight onward replies are in
// and sending someone to the form would be pointless, so nothing links to it.
const PHASE_FOCUS = {
  invitation: { route: 'rsvp', label: 'Continue to RSVP' },
  approaching: { route: 'timeline', label: 'See the Schedule' },
  eventday: { route: 'timeline', label: "See Today's Schedule" },
  live: { route: 'livestream', label: 'Watch the Livestream' },
  after: { route: 'gallery', label: 'See the Gallery' },
};

export function focusFor(phase) {
  return PHASE_FOCUS[phase] || PHASE_FOCUS.invitation;
}
